import { TableCell, TableRow } from '@material-ui/core';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FullNotificationDataFragment, NotificationChannel } from '../../../api';
import useCommonStyles from '../../../useCommonStyles';
import getNotificationType from '../../../utilities/getNotificationType';

export type NotificationRowProps = {
    contactEmail?: string;
    notice: FullNotificationDataFragment;
};

const NotificationRow = ({ contactEmail, notice }: NotificationRowProps) => {
    const { t } = useTranslation(['notifications', 'common']);
    const commonStyles = useCommonStyles();

    const getNotificationDetails = useCallback(
        (notice: FullNotificationDataFragment): string => {
            switch (notice.__typename) {
                case 'AwardedNoticeNotification':
                    return t('notifications:details.awardedNotice');

                case 'AwardedNoticeToSCNotification':
                    return t('notifications:details.awardedNoticeToSC');

                case 'HandoverDateChangeNoticeNotification':
                    return t('notifications:details.handoverDateChange');

                case 'PendingAcceptanceNoticeNotification':
                    return t('notifications:details.pendingAcceptance');

                case 'PendingValuationNoticeNotification':
                    return t('notifications:details.pendingValuation');

                case 'SignUpApprovalNotification':
                    return t('notifications:details.signUpApproval');

                case 'SignUpRejectNotification':
                    return t('notifications:details.signUpReject', {
                        contact: contactEmail,
                    });

                case 'UpcomingBiddingNoticeNotification':
                    return t('notifications:details.upcomingBidding');

                case 'PurchaseTransactionApprovalNoticeNotification':
                case 'SalesTransactionApprovalNoticeNotification':
                    return t('notifications:details.pendingApproval');

                case 'PurchaseTransactionApprovedNoticeNotification':
                case 'SalesTransactionApprovedNoticeNotification':
                    return t('notifications:details.transactionApproved');

                case 'PurchaseTransactionRejectedNoticeNotification':
                case 'SalesTransactionRejectedNoticeNotification':
                    return t('notifications:details.transactionRejected');

                case 'SalesTransactionBroadcastedNoticeNotification':
                    return t('notifications:details.transactionBroadcasted', { content: notice.content });

                case 'SalesTransactionCommentedNoticeNotification':
                    return t('notifications:details.transactionCommented', { content: notice.content });

                case 'DealersOvertakenByHigherBidNotification':
                    return t('notifications:details.higherOfferNotification');

                case 'BidPeriodChangesNotification':
                    return t('notifications:details.bidPeriodChanges');

                case 'BidSubmissionConfirmationNotification':
                    return t('notifications:details.bidSubmissionConfirmation');

                case 'HighestOfferNotification':
                    return t('notifications:details.highestOfferNotification');

                default:
                    return '';
            }
        },
        [t, contactEmail]
    );

    const getTransactionNo = useCallback((note: FullNotificationDataFragment) => {
        switch (note.__typename) {
            case 'AwardedNoticeNotification':
            case 'AwardedNoticeToSCNotification':
            case 'UpcomingBiddingNoticeNotification':
            case 'DealersOvertakenByHigherBidNotification':
            case 'BidPeriodChangesNotification':
            case 'BidSubmissionConfirmationNotification':
            case 'HighestOfferNotification':
                return note.saleTransactions.map(st => st.identifier).join(', ');

            case 'HandoverDateChangeNoticeNotification':
            case 'PendingValuationNoticeNotification':
            case 'PendingAcceptanceNoticeNotification':
            case 'PurchaseTransactionApprovalNoticeNotification':
            case 'PurchaseTransactionApprovedNoticeNotification':
            case 'PurchaseTransactionRejectedNoticeNotification':
                return note.purchaseTransaction.identifier;

            case 'SalesTransactionApprovalNoticeNotification':
            case 'SalesTransactionApprovedNoticeNotification':
            case 'SalesTransactionRejectedNoticeNotification':
            case 'SalesTransactionBroadcastedNoticeNotification':
            case 'SalesTransactionCommentedNoticeNotification':
                return note.salesTransaction?.identifier;

            default:
                return '';
        }
    }, []);

    const getChannels = useCallback(
        () =>
            notice.channels?.map(channel => {
                switch (channel) {
                    case NotificationChannel.Email:
                        return t('notifications:channel.email');
                    case NotificationChannel.Push:
                        return t('notifications:channel.pushNotification');
                    case NotificationChannel.Sms:
                        return t('notifications:channel.sms');
                    case NotificationChannel.WhatsApp:
                        return t('notifications:channel.whatsApp');
                    default:
                        return '';
                }
            }),
        [notice.channels, t]
    );

    return (
        <TableRow className={commonStyles.tableRow}>
            <TableCell>{getTransactionNo(notice)}</TableCell>
            <TableCell>
                {t('common:formats.longDateTime', {
                    date: new Date(notice.createdAt),
                })}
            </TableCell>
            <TableCell>{notice.user?.mobilePhone.number}</TableCell>
            <TableCell>{getNotificationType(notice.type)}</TableCell>
            <TableCell>{getChannels()?.join(', ')}</TableCell>
            <TableCell>{getNotificationDetails(notice)}</TableCell>
        </TableRow>
    );
};

export default NotificationRow;
